import { Bell, Moon, Plus, Search, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStores } from '../state/store-context.js';
import { NotificationBell } from './NotificationBell.js';

function ThemeToggle(): JSX.Element {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const stored = typeof document !== 'undefined'
      ? (document.documentElement.getAttribute('data-theme') as 'dark' | 'light' | null)
      : null;
    return stored ?? 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('ebayds.theme', theme);
    } catch {
      /* private mode etc — ignore */
    }
  }, [theme]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('ebayds.theme') as 'dark' | 'light' | null;
      if (stored === 'dark' || stored === 'light') setTheme(stored);
    } catch {
      /* ignore */
    }
  }, []);

  const btnBase: React.CSSProperties = {
    width: 26,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
  };

  return (
    <div
      style={{
        display: 'flex',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 2,
      }}
    >
      <button
        type="button"
        aria-label="Light theme"
        onClick={() => setTheme('light')}
        style={{
          ...btnBase,
          background: theme === 'light' ? 'var(--bg-card-hi)' : 'transparent',
          color: theme === 'light' ? 'var(--text)' : 'var(--text-muted)',
        }}
      >
        <Sun size={13} strokeWidth={2} />
      </button>
      <button
        type="button"
        aria-label="Dark theme"
        onClick={() => setTheme('dark')}
        style={{
          ...btnBase,
          background: theme === 'dark' ? 'var(--bg-card-hi)' : 'transparent',
          color: theme === 'dark' ? 'var(--text)' : 'var(--text-muted)',
        }}
      >
        <Moon size={13} strokeWidth={2} />
      </button>
    </div>
  );
}

function StorePicker(): JSX.Element {
  const { stores, selectedStoreId, setSelected } = useStores();
  if (stores.length === 0) {
    return (
      <Link
        to="/settings"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          padding: '6px 10px',
          background: 'var(--primary)',
          color: 'var(--primary-on)',
          borderRadius: 7,
          textDecoration: 'none',
          fontWeight: 500,
        }}
      >
        <Plus size={13} strokeWidth={2.4} />
        Connect store
      </Link>
    );
  }

  type Option = { id: string; label: string };
  const opts: Option[] = stores.map((s) => ({ id: s.id, label: s.storeName }));
  if (stores.length > 1) opts.unshift({ id: 'all', label: 'Both stores' });

  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 2,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 7,
        gap: 1,
      }}
    >
      {opts.map((o) => {
        const active = selectedStoreId === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => setSelected(o.id)}
            style={{
              fontSize: 11,
              padding: '4px 10px',
              borderRadius: 5,
              fontFamily: 'var(--font-mono)',
              background: active ? 'var(--bg-card-hi)' : 'transparent',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              fontWeight: 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Topbar(): JSX.Element {
  return (
    <header
      style={{
        height: 'var(--topbar-h)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 28px',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        background: 'color-mix(in srgb, var(--bg) 70%, transparent)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        zIndex: 50,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 7,
          padding: '6px 10px',
          width: 320,
          color: 'var(--text-muted)',
          fontSize: 12,
        }}
      >
        <Search size={13} strokeWidth={2} />
        <input
          type="text"
          placeholder="Search products, stores, trends…"
          style={{
            background: 'none',
            border: 'none',
            outline: 'none',
            color: 'var(--text)',
            flex: 1,
            fontSize: 12,
          }}
        />
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: 10,
            color: 'var(--text-dim)',
            background: 'var(--bg-card-hi)',
            padding: '1px 5px',
            borderRadius: 3,
            border: '1px solid var(--border)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          ⌘K
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <StorePicker />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 10px',
            background: 'var(--success-bg)',
            color: 'var(--success)',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--success)',
              position: 'relative',
              display: 'inline-block',
            }}
          >
            <span
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'var(--success)',
                animation: 'dot-pulse 1.6s ease-out infinite',
              }}
            />
          </span>
          All systems live
        </div>
        <ThemeToggle />
        <NotificationBell />
      </div>
    </header>
  );
}
